import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, Gift } from 'lucide-react';
import axiosInstance from '../api/axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

export default function NewsletterConfirm() {
  const { token } = useParams();
  const [state, setState] = useState('loading'); // loading | success | already | error
  const [message, setMessage] = useState('');
  const [discountCode, setDiscountCode] = useState('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axiosInstance.get(`/newsletter/confirm/${token}`);
        if (!mounted) return;
        setDiscountCode(res.data?.discount_code || '');
        setMessage(res.data?.message || '');
        setState(res.data?.already_confirmed ? 'already' : 'success');
      } catch (e) {
        if (!mounted) return;
        setMessage(e.response?.data?.detail || 'Bestätigung fehlgeschlagen. Bitte melden Sie sich erneut an.');
        setState('error');
      }
    })();
    return () => { mounted = false; };
  }, [token]);

  const isOk = state === 'success' || state === 'already';

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" style={{ backgroundColor: '#E8E0D5' }} data-testid="newsletter-confirm-page">
      <Card className="max-w-md w-full rounded-none border-2" style={{ borderColor: '#C4A084' }}>
        <CardContent className="p-8 text-center">
          {state === 'loading' && (
            <>
              <Loader2 className="h-14 w-14 mx-auto mb-4 animate-spin" style={{ color: '#C4A084' }} />
              <h1 className="text-2xl font-semibold mb-2" style={{ color: '#5D4E3C' }}>Bestätigung läuft…</h1>
              <p className="text-gray-600 text-sm">Einen Augenblick bitte.</p>
            </>
          )}

          {isOk && (
            <>
              <CheckCircle2 className="h-16 w-16 mx-auto mb-4" style={{ color: '#C4A084' }} data-testid="newsletter-confirm-success-icon" />
              <h1 className="text-2xl font-semibold mb-3" style={{ color: '#5D4E3C' }}>
                {state === 'already' ? 'Bereits bestätigt' : 'Willkommen!'}
              </h1>
              <p className="text-gray-700 mb-4">{message}</p>

              {discountCode && (
                <div className="my-6 px-5 py-5" style={{ background: '#FAF5EE', border: '2px dashed #C4A084' }}>
                  <div className="flex items-center justify-center gap-2 mb-2 text-xs uppercase tracking-widest" style={{ color: '#8B7355' }}>
                    <Gift className="h-4 w-4" /> Ihr Willkommens-Rabatt
                  </div>
                  <div
                    data-testid="newsletter-confirm-code"
                    className="font-mono font-bold tracking-widest"
                    style={{ fontSize: '26px', color: '#5D4E3C' }}
                  >
                    {discountCode}
                  </div>
                  <div className="text-xs mt-2" style={{ color: '#6B5D4F' }}>
                    10 % Rabatt ab 30 € Bestellwert
                  </div>
                </div>
              )}

              <Link to="/products">
                <Button
                  className="rounded-none w-full text-white"
                  style={{ backgroundColor: '#5D4E3C' }}
                  data-testid="newsletter-confirm-shop-btn"
                >
                  Jetzt stöbern
                </Button>
              </Link>
            </>
          )}

          {state === 'error' && (
            <>
              <XCircle className="h-16 w-16 mx-auto mb-4 text-rose-500" data-testid="newsletter-confirm-error-icon" />
              <h1 className="text-2xl font-semibold mb-3" style={{ color: '#5D4E3C' }}>
                Bestätigung nicht möglich
              </h1>
              <p className="text-gray-700 mb-6">{message}</p>
              <Link to="/newsletter">
                <Button
                  className="rounded-none w-full text-white"
                  style={{ backgroundColor: '#8B7355' }}
                  data-testid="newsletter-confirm-retry-btn"
                >
                  Erneut anmelden
                </Button>
              </Link>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
